import { forwardRef } from 'react';
import { Trophy } from 'lucide-react';
import logoIcon from '../../assets/logo-icon.png';
import type { RankingPlayer, RankingLeaderboardMetric } from '../../types';
import { leaderboardPosition } from '../../utils/leaderboard';

/** The two aspect ratios Instagram accepts: a full-screen story and a feed post. */
export type ShareCardFormat = 'story' | 'feed';

export const SHARE_CARD_SIZES: Record<ShareCardFormat, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  feed: { width: 1080, height: 1350 },
};

interface RankingTop5ShareCardProps {
  rankingName: string;
  /** The last round that fed these standings. null hides the eyebrow. */
  roundNumber: number | null;
  imageUrl: string | null;
  players: RankingPlayer[];
  metric: RankingLeaderboardMetric;
  format: ShareCardFormat;
}

const METRIC_LABELS: Record<RankingLeaderboardMetric, string> = {
  total_points: 'Pontos',
  matches_won: 'Vitórias',
  win_rate: '% Vitórias',
  games_won: 'Games',
  game_balance: 'Saldo',
  matches_played: 'Partidas',
  matches_lost: 'Derrotas',
  rounds_played: 'Rodadas',
};

function getMetricValue(player: RankingPlayer, metric: RankingLeaderboardMetric): string {
  if (metric === 'win_rate') {
    const rate = player.matchesPlayed > 0 ? (player.matchesWon / player.matchesPlayed) * 100 : 0;
    return `${rate.toFixed(1)}%`;
  }
  if (metric === 'game_balance') {
    const balance = player.gamesWon - player.gamesLost;
    return balance > 0 ? `+${balance}` : `${balance}`;
  }
  const map: Record<string, number> = {
    total_points: player.totalPoints,
    matches_won: player.matchesWon,
    matches_lost: player.matchesLost,
    matches_played: player.matchesPlayed,
    games_won: player.gamesWon,
    rounds_played: player.roundsPlayed,
  };
  return `${map[metric] ?? 0}`;
}

const DISPLAY_FONT = "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', system-ui, sans-serif";
const GRADIENT_TEXT = {
  background: 'linear-gradient(135deg, #06B6D4 0%, #FBD85D 50%, #FF6B6B 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
} as const;

/** Podium colours for the position badge — gold, silver, bronze, then turquoise. */
const BADGE_STYLES = [
  { background: 'rgba(251,216,93,.14)', border: '2px solid rgba(251,216,93,.45)', color: '#FBD85D' },
  { background: 'rgba(255,255,255,.08)', border: '2px solid rgba(255,255,255,.35)', color: 'rgba(255,255,255,.75)' },
  { background: 'rgba(245,158,11,.14)', border: '2px solid rgba(245,158,11,.45)', color: '#F59E0B' },
  { background: 'rgba(6,182,212,.10)', border: '2px solid rgba(6,182,212,.30)', color: '#22D3EE' },
  { background: 'rgba(6,182,212,.10)', border: '2px solid rgba(6,182,212,.30)', color: '#22D3EE' },
];

/**
 * Per-format metrics. The story reserves Instagram's UI safe zones (210px of
 * chrome at the top, 252px of reply bar at the bottom); the feed post has no
 * overlay to dodge, so it uses an even margin instead.
 */
const LAYOUT = {
  story: {
    inset: { top: 210, bottom: 252 },
    titleMarginTop: 32,
    topSize: 128,
    fiveSize: 230,
    eyebrowSize: 34,
    cardPadding: '34px 44px',
    cardMarginTop: 28,
    headMarginBottom: 22,
    columns: '56px 80px 1fr auto',
    rowGap: 22,
    badgeSize: 56,
    badgeFont: 30,
    avatarSize: 80,
    nameSize: 34,
    valueSize: 44,
    dividerMargin: 20,
    footerSize: 32,
  },
  feed: {
    inset: { top: 64, bottom: 56 },
    titleMarginTop: 20,
    topSize: 110,
    fiveSize: 190,
    eyebrowSize: 32,
    cardPadding: '28px 40px',
    cardMarginTop: 20,
    headMarginBottom: 18,
    columns: '52px 72px 1fr auto',
    rowGap: 20,
    badgeSize: 52,
    badgeFont: 28,
    avatarSize: 72,
    nameSize: 32,
    valueSize: 42,
    dividerMargin: 16,
    footerSize: 30,
  },
} as const;

/**
 * The Top 5 leaderboard rendered at its native export size (1080×1920 for
 * stories, 1080×1350 for feed posts), so `domToCanvas` can capture it at 1x.
 * Every image must already be inlined as a data URL by the caller — the canvas
 * capture cannot reach cross-origin assets.
 */
export const RankingTop5ShareCard = forwardRef<HTMLDivElement, RankingTop5ShareCardProps>(
  ({ rankingName, roundNumber, imageUrl, players, metric, format }, ref) => {
    const top5 = players.slice(0, 5);
    const size = SHARE_CARD_SIZES[format];
    const l = LAYOUT[format];
    const eyebrow = roundNumber != null ? `RODADA ${roundNumber}` : '';
    const trimmedName = rankingName.trim();
    const displayName = /^ranking\b/i.test(trimmedName) ? trimmedName : `Ranking ${trimmedName}`;

    const header = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={logoIcon} alt="" style={{ height: 48, width: 'auto' }} />
          <span style={{ ...GRADIENT_TEXT, fontFamily: DISPLAY_FONT, fontSize: 34, fontWeight: 800 }}>
            BT Vision
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <div
            style={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: '50%',
              overflow: 'hidden',
              background: 'rgba(255,255,255,.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {imageUrl ? (
              <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Trophy style={{ width: 26, height: 26, color: '#22D3EE' }} />
            )}
          </div>
          <span
            style={{
              fontSize: 29,
              fontWeight: 600,
              color: 'rgba(255,255,255,.75)',
              maxWidth: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayName}
          </span>
        </div>
      </div>
    );

    const topFive = (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: format === 'story' ? 28 : 24 }}>
        <span
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: l.topSize,
            fontWeight: 800,
            lineHeight: 1.1,
            color: 'rgba(255,255,255,.16)',
          }}
        >
          TOP
        </span>
        <span
          style={{ ...GRADIENT_TEXT, fontFamily: DISPLAY_FONT, fontSize: l.fiveSize, fontWeight: 800, lineHeight: 1 }}
        >
          5
        </span>
      </div>
    );

    const eyebrowNode = eyebrow ? (
      <span style={{ fontSize: l.eyebrowSize, fontWeight: 600, letterSpacing: '.3em', color: '#F4E4C1' }}>
        {eyebrow}
      </span>
    ) : null;

    return (
      <div
        ref={ref}
        style={{
          position: 'relative',
          width: size.width,
          height: size.height,
          background: '#0F172A',
          overflow: 'hidden',
          flexShrink: 0,
          fontFamily: BODY_FONT,
          color: '#FFFFFF',
        }}
      >
        {/* beach-pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: [
              'radial-gradient(circle at 20% 80%, rgba(255,107,107,0.08) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(6,182,212,0.08) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 40%, rgba(251,216,93,0.04) 0%, transparent 50%)',
            ].join(','),
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: l.inset.top,
            bottom: l.inset.bottom,
            left: 64,
            right: 64,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {header}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Title — stacked in the story, side by side in the shorter feed post */}
          {format === 'story' ? (
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: l.titleMarginTop }}>
              {eyebrowNode}
              {topFive}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginTop: l.titleMarginTop,
              }}
            >
              {topFive}
              {eyebrowNode}
            </div>
          )}

          {/* glass-card */}
          <div
            style={{
              background: 'rgba(255,255,255,.03)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 32,
              padding: l.cardPadding,
              marginTop: l.cardMarginTop,
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: l.headMarginBottom,
              }}
            >
              <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '.3em', color: 'rgba(255,255,255,.4)' }}>
                CLASSIFICAÇÃO
              </span>
              <span style={{ fontSize: 24, fontWeight: 600, letterSpacing: '.3em', color: 'rgba(255,255,255,.4)' }}>
                {METRIC_LABELS[metric].toUpperCase()}
              </span>
            </div>

            {top5.map((player, index) => {
              const isFirst = index === 0;
              const badge = BADGE_STYLES[index] ?? BADGE_STYLES[4];

              return (
                <div key={player.id}>
                  {index > 0 && (
                    <div
                      style={{
                        borderTop: '1px solid rgba(255,255,255,.09)',
                        margin: `${l.dividerMargin}px 0`,
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: l.columns,
                      gap: l.rowGap,
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        ...badge,
                        fontFamily: DISPLAY_FONT,
                        width: l.badgeSize,
                        height: l.badgeSize,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 14,
                        fontSize: l.badgeFont,
                        fontWeight: 800,
                      }}
                    >
                      {leaderboardPosition(player, index, metric)}
                    </span>

                    <div
                      style={{
                        width: l.avatarSize,
                        height: l.avatarSize,
                        flexShrink: 0,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, rgba(6,182,212,0.3), rgba(255,107,107,0.3))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {player.avatarUrl ? (
                        <img
                          src={player.avatarUrl}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span style={{ fontFamily: DISPLAY_FONT, fontSize: l.badgeFont, fontWeight: 700, color: '#fff' }}>
                          {player.name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      )}
                    </div>

                    <span
                      style={{
                        fontSize: l.nameSize,
                        fontWeight: isFirst ? 700 : 600,
                        color: isFirst ? '#FFFFFF' : 'rgba(255,255,255,.85)',
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {player.name || 'Jogador'}
                    </span>

                    <span
                      style={{
                        fontFamily: DISPLAY_FONT,
                        fontSize: l.valueSize,
                        fontWeight: 800,
                        color: isFirst ? '#FBD85D' : '#22D3EE',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {getMetricValue(player, metric)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          </div>

          <div
            style={{
              marginTop: 'auto',
              fontSize: l.footerSize,
              fontWeight: 700,
              color: '#22D3EE',
              letterSpacing: '.04em',
              textAlign: 'center',
            }}
          >
            btvision.app
          </div>
        </div>
      </div>
    );
  }
);

RankingTop5ShareCard.displayName = 'RankingTop5ShareCard';
